<script setup>
import { ref, computed, inject, nextTick } from 'vue'
import { useStore } from 'vuex'
import cdnFile from '@/helpers/cdnFile.js'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useLoading } from 'vue-loading-overlay'
import { useToast } from 'vue-toast-notification'
import ButtonComponent from '../components/ButtonComponent.vue'
import AppHeader from '../components/AppHeader.vue'
import { useMixpanel } from '@/composables/mixpanel'
import queryString from 'query-string'
import SvgIcon from '@/components/SvgIcon.vue'
import { getSrcSet } from '@/helpers/srcSetHelper'
import FooterComponent from '@/components/Footer.vue'

const modelRef = ref(null)

const mixpanel = useMixpanel()
const $route = useRoute()

import(
  `../assets/data/premade/premade-${$route.params.modelName[0].toUpperCase()}${$route.params.modelName.slice(1)}.json`
).then((module) => {
  modelRef.value = module.default
  modelRef.value.src = getSrcSet('models', modelRef.value.model.name)
})

import(
  `../assets/data/scenarios/scenarios-${$route.params.modelName[0].toUpperCase()}${$route.params.modelName.slice(1)}.json`
).then((module) => {
  scenarios.value = module.default
})

const loader = ref(null)
const $store = useStore()

const $loading = useLoading({
  canCancel: false,
  isFullPage: true,
  color: '#a15cff',
})
const $toast = useToast()
const $axios = inject('axios')

const $router = useRouter()

const user = computed(() => $store.state.user)
const discountPopupOpen = computed(() => $store.state.discountPopupOpen)
const premadeModel = computed(() => $store.state.premadeModel)

const excludedKeys = new Set([
  'name',
  'gender',
  'random',
  'voiceId',
  'referenceImage',
  'premadeId',
  'persona',
])

const modelAttributes = computed(() => {
  if (!modelRef.value) return []

  return Object.entries(modelRef.value.model)
    .filter(([key]) => !excludedKeys.has(key))
    .map(([label, value]) => ({ label, value }))
})

useHead(
  computed(() => {
    if (!modelRef.value)
      return {
        meta: [{ name: 'robots', content: 'noindex' }],
      }

    const modelGender =
      modelRef.value.model.style == 'anime'
        ? 'Hentai'
        : modelRef.value.model.gender === 'female'
          ? 'Girlfriend'
          : 'Boyfriend'

    const modelBaseUrl =
      modelRef.value.model.style == 'anime'
        ? '/ai-hentai'
        : modelRef.value.model.gender === 'female'
          ? '/'
          : '/ai-boyfriend'

    const headTitle = `${import.meta.env.VITE_BRAND_NAME} | AI ${modelGender} | ${modelRef.value.model.name}`
    const headDescription = modelRef.value.model.persona.split('\n\n')[0]
    const headUrl = `https://${import.meta.env.VITE_DOMAIN_NAME}/${modelRef.value.model.style == 'anime' ? 'ai-hentai' : modelRef.value.model.gender === 'female' ? 'ai-girlfriend' : 'ai-boyfriend'}/${modelRef.value.model.name.toLowerCase()}`
    const headImage = cdnFile(modelRef.value.model.referenceImage)
    const headLogo = `https://${import.meta.env.VITE_DOMAIN_NAME}/logo.png`

    return {
      title: headTitle,
      meta: [
        { name: 'description', content: headDescription },

        { property: 'og:title', content: headTitle },
        { property: 'og:description', content: headDescription },
        { property: 'og:url', content: headUrl },
        { property: 'og:image', content: headImage },
        { property: 'og:logo', content: headLogo },

        { name: 'twitter:title', content: headTitle },
        { name: 'twitter:description', content: headDescription },
        { name: 'twitter:url', content: headUrl },
        { name: 'twitter:image', content: headImage },

        { name: 'robots', content: 'noindex' },
      ],
      script: [
        {
          type: 'application/ld+json',
          innerHTML: `{ "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "AI ${modelGender}", "item": "https://${import.meta.env.VITE_DOMAIN_NAME}${modelBaseUrl}" }, { "@type": "ListItem", "position": 2, "name": "${modelRef.value.model.name}", "item": "${headUrl}" }] }`,
        },
      ],
    }
  }),
)

const redirectToGenerator = () => {
  const redirectQuery = queryString.stringify({
    modelId: encodeURIComponent(modelRef.value.model.premadeId),
  })

  $router.push('/nsfw-ai-image-generator' + '?' + redirectQuery)
}

async function createModel(scenario) {
  const model = {
    ...modelRef.value.model,
    personaInitialSetting: scenario.initialSetting,
    personaInitialMessage: scenario.initialMessage,
    userPersona: user.value ? user.value.persona : null,
    userName: user.value ? user.value.name : null,
    userGender: user.value ? user.value.gender : null,
  }

  if (!user.value && premadeModel.value?.premadeId === model.premadeId) {
    $store.commit('SET_SELECTED_MODEL', model.id)
    await $router.push('/chat')
  } else if (user.value) {
    const userModel = user.value.models.filter(
      (el) =>
        el.premadeId === model.premadeId &&
        el.personaInitialMessage === model.personaInitialMessage,
    )
    if (userModel.length) {
      $store.commit('SET_SELECTED_MODEL', userModel[0].id)
      await $router.push('/chat')
    } else {
      loader.value = $loading.show()
      await $axios
        .post('/user/create-model/premade', {
          model,
        })
        .then(({ data: { model: createdModel } }) => {
          $store.commit('ADD_MODEL', createdModel)
          $store.commit('SET_SELECTED_MODEL', createdModel.id)
          $store.commit('SET_MESSAGES', createdModel.messages)
          $router.push('/chat')
        })
        .catch((e) => {
          const message = e?.response?.data?.message
          return $toast.error(message || 'An error occurred')
        })
      loader.value.hide()
    }
  } else {
    await $store.commit('SET_PREMADE_MODEL', model)
    await $store.commit('SET_SELECTED_MODEL', model.premadeId)
    await $router.push('/chat')
    await $axios
      .post('/model/create/premade', {
        model,
      })
      .then(({ data: { model: createdModel, changedLuna } }) => {
        $store.commit('SET_PREMADE_MODEL', createdModel)
        $store.commit('SET_SELECTED_MODEL', createdModel.id)
      })
      .catch((e) => {
        const message = e?.response?.data?.message
        return $toast.error(message || 'An error occurred')
      })
  }
}

function startUpperCase(text) {
  if (typeof text === 'string') {
    return text.charAt(0).toUpperCase() + text.slice(1)
  }
  return text
}

function toStartCase(text) {
  const result = text.replace(/([A-Z])/g, ' $1')
  return startUpperCase(result)
}

const scenarios = ref([])

const suggestNewScenarioLink = computed(() => {
  const email = import.meta.env.VITE_SUPPORT_EMAIL
  const subject = `I want to suggest a new scenario for ${modelRef.value.model.name}`
  const body =
    'Please let us know what scenario(s) you want to see and your feedback will be anonymously considered:'

  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
})

import('@/views/Chat/Chat.vue')
import('@/views/Generator.vue')
</script>

<template>
  <div class="lg:ml-[280px]" :class="discountPopupOpen ? 'pt-16' : ''">
    <AppHeader />
    <div
      class="px-4 xl:px-16 3xl:mx-auto m-auto max-w-[1450px]"
      v-if="modelRef && scenarios"
    >
      <div
        class="lg:py-[27px] lg:px-[35px] lg:bg-[#0A0D22] bg-opacity-50 rounded-[25px] lg:border border-[#141A3D]"
      >
        <div class="flex flex-col lg:flex-row gap-[0] lg:gap-[43px]">
          <div class="lg:hidden flex flex-col text-center">
            <h1 class="text-[26px] leading-[35px] mb-[10px] lg:mb-[25px]">
              {{ modelRef.model.name }}
            </h1>
          </div>
          <div
            class="self-center lg:self-auto relative overflow-hidden cursor-pointer w-full mb-[25px] lg:mb-0 min-w-[300px] max-w-[75vw] sm:min-w-unset lg:w-[316px] sm:max-w-full sm:w-[calc((100%-1.5rem)/2)] md:w-[calc((100%-3rem)/2)] xl:w-[calc((100%-4.5rem)/4)]"
          >
            <picture class="flex justify-center">
              <source
                :srcset="modelRef.src.mobileAvif"
                media="(max-width: 450px)"
                type="image/avif"
              />
              <source
                :srcset="modelRef.src.mobileWebp"
                media="(max-width: 450px)"
                type="image/webp"
              />

              <source
                :srcset="modelRef.src.desktopAvif"
                media="(min-width: 451px)"
                type="image/avif"
              />
              <source
                :srcset="modelRef.src.desktopWebp"
                media="(min-width: 451px)"
                type="image/webp"
              />
              <img
                class="lg:w-full w-[calc((100vw-1rem)/2)] aspect-[832/1216] rounded-[20px] no-image-select"
                :src="modelRef.src.default"
                :alt="modelRef.model.name"
                draggable="false"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            </picture>
          </div>
          <div class="flex-col justify-start align-baseline h-full w-full">
            <h1 class="text-[26px] leading-[35px] mb-[20px] hidden lg:flex">
              {{ modelRef.model.name }}
            </h1>
            <div class="flex flex-col gap-[25px]">
              <div>
                <!-- <h2 class="flex items-center gap-3 mb-4">
                  <SvgIcon
                    sprite="main"
                    icon-id="chat"
                    class="h-[24px] w-[24px]"
                  />
                  Chat
                </h2> -->
                <div class="flex flex-col gap-4">
                  <button
                    v-for="scenario of scenarios"
                    class="bg-[#0E132D] rounded-xl p-3 border border-[#141A3D] flex flex-col items-start justify-start text-left gap-2"
                    @click="createModel(scenario)"
                  >
                    <h3 class="text-sm text-[#B1B5DB] flex gap-2 items-center">
                      <SvgIcon
                        sprite="main"
                        icon-id="chat-gray-modeldetails"
                        class="h-[20px] w-[20px]"
                      />
                      {{ scenario.title }}
                    </h3>
                    <p>
                      {{ scenario.initialSetting }}
                    </p>
                  </button>
                  <a
                    v-if="user"
                    class="bg-[#0E132D] rounded-xl p-3 border border-[#141A3D] flex flex-col items-center justify-start text-left gap-2"
                    :href="suggestNewScenarioLink"
                  >
                    Suggest new scenario
                  </a>
                </div>
              </div>
              <ButtonComponent
                type="button"
                variant="secondary"
                :borderVisible="false"
                class="flex gap-3 py-[14px] pl-[18px] px-[26px] w-full items-center justify-center rounded-xl"
                @click="redirectToGenerator"
              >
                <SvgIcon
                  sprite="generator"
                  icon-id="generator"
                  class="h-[24px] w-[24px]"
                />
                Generate Picture
              </ButtonComponent>
            </div>
          </div>
        </div>

        <h2 class="mt-[30px] lg:mt-[50px] mb-2 lg:mb-3 text-xl">Persona</h2>
        <div
          class="text-sm lg:text-base [&>p:not(:last-child)]:mb-[10px] leading-relaxed lg:leading-relaxed text-justify text-[#B1B5DB]"
          v-html="
            `<p>` + modelRef.model.persona.replace(/\n\n/g, '</p><p>') + `</p>`
          "
        ></div>

        <h2 class="mt-[30px] lg:mt-[50px] mb-2 lg:mb-3 text-xl">Traits</h2>
        <div
          class="w-full grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-[10px]"
        >
          <template v-for="attr in modelAttributes" :key="attr.value">
            <div
              class="flex justify-center items-center bg-[#0E132D] gap-[10px] py-[9px] px-[8px] rounded-xl border border-[#141A3D]"
            >
              <span
                class="flex justify-center items-center w-[34px] h-[34px] rounded-xl"
                style="
                  background: rgb(10, 13, 30);
                  box-shadow:
                    rgb(30, 26, 61) 0px 0px 0px 1px,
                    rgb(19, 20, 46) 0px 0px 10px 2px inset;
                "
              >
                <SvgIcon icon-id="checkmark" class="w-[14px] h-auto" />
              </span>
              <div class="flex-1 flex flex-col">
                <p class="text-sm text-[#B1B5DB]">
                  {{ toStartCase(attr.label) }}
                </p>
                <h3 class="text-[14px] leading-5">
                  {{
                    Array.isArray(attr.value) && attr.value.length > 0
                      ? startUpperCase(attr.value[0])
                      : startUpperCase(attr.value)
                  }}
                </h3>
              </div>
            </div>
          </template>
        </div>
      </div>
      <FooterComponent></FooterComponent>
    </div>
  </div>
</template>
